import { AppPreviewDialogComponent } from '../app-preview-dialog/app-preview-dialog.component';
import { DocumentDeleteDialogComponent } from '../document-delete-dialog/document-delete-dialog.component';
import { Borrower } from 'app/modules/borrower/models/borrower/borrower.model';
import { Component, Input, ChangeDetectionStrategy, ViewChild, OnChanges, Output, EventEmitter, OnInit, ElementRef, ChangeDetectorRef, SimpleChanges, TemplateRef } from '@angular/core';
import { Cookie } from 'ng2-cookies/ng2-cookies';
import { Document } from 'app/models/dto/document.model';
import { DocumentCategory } from 'app/models/dto/document-category.model';
import { environment } from 'environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import * as _ from 'lodash';
import { fileTypesAccepted } from 'app/models/const/fileTypesAccepted';
import { fadeIn } from 'ng-animate';
import { Select } from '@ngxs/store';
import { trigger, transition, useAnimation } from '@angular/animations';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { DocumentUploadDialogComponent } from '../document-upload-dialog/document-upload-dialog.component';
import { map, Observable } from 'rxjs';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'ng2-app/models/dto/lender-user.model';
import { format, parse, sub, toDate } from 'date-fns';
import { Alert } from 'app/models/dto/alert.model';

@Component({
    selector: 'app-documents',
    templateUrl: './documents.component.html',
    styleUrls: ['./documents.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('animateIn', [
            transition(':enter',
                useAnimation(fadeIn, { params: { timing: 0.3 } })
            )
        ])
    ]
})
export class DocumentsComponent implements OnChanges, OnInit {

    @Input() borrower: Borrower;
    @Input() documents: Document[];
    @Input() canProcessDocs: boolean;
    @Input() canSoftPullCredit: boolean;
    @Input() canUseDRSDocumentVerify: boolean;
    @Input() sending: boolean;
    @Input() creditPullDisableMessage: string;
    @Input() isUploading: boolean;
    @Input() isPullingCredit: boolean;
    @Input() uploadedPercent: number;
    @Input() uploadProgress: string;
    @Input() documentCategories: DocumentCategory[];
    @Input() documentAlert: { level: string, message: string, created: number };
    @Input() ocrolusAlert: Alert | null;
    @Input() creditAlert: { level: string, message: string };
    @Input() isFundingDesk: boolean;
    @Input() viewableMimetypes: string[];
    @Input() tableColumns: string[];
    @Input() showActions: boolean = true;
    @Input() showUploadedBy: boolean = true;
    @Input() defaultPageSize: number = 100;
    @Input() showPageSizeOptions: boolean = true;

    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    @ViewChild('editInput') editInput: ElementRef;
    @ViewChild('docUploadLimitDialog') docUploadLimitDialog: TemplateRef<any>;

    @Output() updateDoc = new EventEmitter<Document>();
    @Output() updateDocDrsVerification = new EventEmitter<Document>();
    @Output() processDocs = new EventEmitter<number[]>();
    @Output() uploadDoc = new EventEmitter<Document[]>();
    @Output() checkOcrolus = new EventEmitter<Document[]>();
    @Output() documentAlertDismissed = new EventEmitter<void>();
    @Output() ocrolusAlertDismissed = new EventEmitter<void>();
    @Output() creditAlertDismissed = new EventEmitter<void>();
    @Output() repullCred = new EventEmitter<number>();
    @Output() triggerExperianCommercial = new EventEmitter<number>();

    @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

    filteredOptions: Observable<DocumentCategory[]>;
    canViewExperianCommercialButton$ = new Observable;
    ocrolusDisable = true;
    ocrolusDisableReason: string | null = 'Select Valid Bank Statements to send to Ocrolus';
    dataSource: MatTableDataSource<Document>;
    selection = new SelectionModel<Document>(true, []);
    selectedCategory: string;
    monthOptions: string[] = [];
    pageSizeOptions: number[] = [20, 50, 100, 150, 200];
    typesAccepted = fileTypesAccepted;
    editDocId: number | null = null;
    editDocForm = new UntypedFormGroup({
        'content': new UntypedFormControl()
    });
    actionId: null | number = null;
    authToken: string = Cookie.get('auth_token');
    searching = false;
    readyToLoad = false;
    documentUploadLimit = 20;

    constructor(
        private dialog: MatDialog,
        private fb: UntypedFormBuilder,
        private cdr: ChangeDetectorRef,
        private dialogRef: MatDialog,
    ) { }

    ngOnInit() {
        this.initDateOptions();
        if (this.lenderUser$) {
            this.canViewExperianCommercialButton$ = this.lenderUser$.pipe(
                map(currentUser => currentUser.permissions.includes('canViewExperianCommercialButton'))
            );
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.documents || changes.documentCategories) {
            if (this.documentCategories.length > 0) {
                this.buildDataSource();
                this.readyToLoad = true;
            }
        }
    }

    buildDataSource(): void {
        this.dataSource = new MatTableDataSource(this.documents);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.dataSource.filterPredicate = this.filterPredicate;
        this.selection.clear();
    }

    filterPredicate(row: Document, filter: string): boolean {
        const patterns = filter
            .toLowerCase()
            .replace(/\W+/g, ' ')
            .split(' ');
        const value = [
            row.title,
            row.category,
            format(toDate(row.createdTimestamp * 1000), 'MM/dd/yyyy'),
            format(toDate(row.createdTimestamp * 1000), 'M/d/yyyy'),
        ].join().toLowerCase();
        const result = _.every(patterns, pattern => value.indexOf(pattern) !== -1);
        return result;
    }

    isAllSelected(): boolean {
        if (this.dataSource) {
            const numSelected = this.selection.selected.length;
            const numRows = this.dataSource.data
                ? this.dataSource.data.filter(row => row.scanStatus === 'CLEAN' || row.scanStatus === null).length
                : 0;
            return numSelected === numRows;
        }
        return false;
    }

    masterToggle(): void {
        // relative to the filtered set (which is "all" if no filter) ...
        // ignoring non-clean documents
        const rows = (
            this.dataSource.filteredData.length
                ? this.dataSource.filteredData
                : this.dataSource.data
        )
            .filter(doc => doc.scanStatus === 'CLEAN' || doc.scanStatus === null);

        // the master toggle should behave as such ...
        if (
            _.every(rows, row => this.selection.isSelected(row)) // all
        ) {
            this.selection.deselect(...rows); // none
        } else { // none || some
            this.selection.select(...rows); // all
        }

        this.checkOcrolusStatus();
    }

    checkboxLabel(row?: Document): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.activityId}`;
    }

    checkOcrolusStatus() {
        const noneSelected = this.selection.selected.length === 0;
        if (noneSelected) {
            this.ocrolusDisable = true;
            this.ocrolusDisableReason = 'Select Valid Bank Statements to send to Ocrolus';
            return;
        }

        const invalidSelected = !!_.find(this.selection.selected, (document) => {
            const statuses = ['QUEUED', 'VERIFYING', 'PROCESSING', 'REJECTED', 'OCROLUS_DELETED', 'VERIFICATION_COMPLETE'];
            const processing = statuses.indexOf(document.ocrolusStatus) >= 0;
            return processing || !document.isOcrolusEligible;
        });

        if (invalidSelected) {
            this.ocrolusDisable = true;
            this.ocrolusDisableReason = 'You have selected a doc that is processing or not eligible for Ocrolus.';
            return;
        }

        this.ocrolusDisable = false;
        this.ocrolusDisableReason = null;
    }

    downloadDocument(documentId: number): void {
        const url = `${environment.API_URL}/document/${documentId}?stream=1&method=attachment&access_token=${this.authToken}`;
        window.open(url, '_blank');
    }

    downloadSelected(): void {
        this.selection.selected.forEach((document, index) => {
            setTimeout(() => {
                this.downloadDocument(document.id);
            }, index * 1000);
        });
        this.selection.clear();
    }

    viewSelected(): void {
        this.selection.selected.forEach((document, index) => {
            setTimeout(() => {
                this.viewDocument(document.id);
            }, index * 500);
        });
        this.selection.clear();
    }

    getCategoryName(category: string) {
        if (this.documentCategories.length > 0) {
            return this.documentCategories.filter(cat => cat.id === category)[0]?.name;
        }
    }

    processDocuments(): void {
        const docIds = this.selection.selected.map(document => document.id);
        this.processDocs.emit(docIds);
        this.documents = this.documents.map( doc => {
          if(docIds.includes(doc.id)) {
            doc.ocrolusStatus = 'PROCESSING';
          }
          return doc;
        });
        this.selection.clear();
    }

    initDateOptions(): void {
        for (let i = 0; i <= 24; i++) {
            this.monthOptions.push(format(sub(new Date(Date.now()), { months: i }), 'MMM yyyy'));
        }
    }

    getMonths(months: string[]): string {
        return months.length > 0 ? '' : 'Choose';
    }

    formatForMonth(date: string): string {
        if (!date) { return ''; }
        return format(new Date(date), 'MM/yyyy');
    }

    formatDisplayMonth(date: string): string {
        return format(parse(date, 'MM/yyyy', new Date()), 'MMM yyyy');
    }

    viewDocument(docId: number): void {
        const url = `${environment.API_URL}/document/${docId}?stream=1&access_token=${Cookie.get('auth_token')}`;
        window.open(url, '_blank');
    }

    getViewUrl(docId: number): string {
        return `${environment.API_URL}/document/${docId}?stream=1&access_token=${Cookie.get('auth_token')}`;
    }

    previewApplication(): void {
        this.dialog.open(AppPreviewDialogComponent, {
            data: { borrowerId: this.borrower.id }
        });
    }

    confirmDelete(documents: Document[]): void {
        this.dialog
            .open(DocumentDeleteDialogComponent, { data: documents })
            .afterClosed().subscribe(() => {
                this.selection.clear();
                this.cdr.markForCheck();
            });
    }

    uploadDocuments(documents): void {
        if (documents.length) {
            if(documents.length > this.documentUploadLimit) {
                this.dialogRef.open(this.docUploadLimitDialog);
                return;
            }
            if (this.isFundingDesk) {
                this.dialogRef.open(DocumentUploadDialogComponent, {
                    width: 'auto',
                    height: 'auto',
                    autoFocus: false,
                    data: {
                        documents: documents,
                        categories: this.documentCategories
                    }
                }).afterClosed().subscribe((data) => {
                    if (data) {
                        this.uploadDoc.emit(data);
                    }
                });
            } else {
                this.uploadDoc.emit(documents);
            }
        }
    }

    updateDocument(document: Document, update: { type: string, value: string | string[] }): void {
        document = { ...document, [update.type]: update.value };
        this.updateDoc.emit(document);
    }

    updateCategory(payload: {document: Document, update: { type: string, value: string }}) {
        this.updateDocument(payload.document, payload.update);
    }

    enterEditMode(document: Document): void {
        if (document.id) {
            this.editDocId = document.id;
            this.editDocForm = this.fb.group({
                content: document.ext && document.ext.length > 1 ?
                    document.title.substr(0, document.title.lastIndexOf('.')) : document.title
            });
            setTimeout(() => {
                this.editInput.nativeElement.focus();
            }, 200);
        }
    }

    editDoc(document: Document): void {
        this.editDocId = null;
        if (document.title === this.editDocForm.value.content || this.editDocForm.value.content === '') {
            this.editDocForm.reset();
            return;
        } else {
            document = { ...document };
            const newTitle = document.ext && document.ext.length > 1
                ? `${this.editDocForm.value.content}${document.ext}`
                : this.editDocForm.value.content;
            document.title = newTitle;
            document.filename = newTitle;
            this.updateDoc.emit(document);
            this.editDocForm.reset();
        }
    }

    documentTrackBy(document: Document): number {
        return document.id;
    }

    repullCredit(): void {
        this.repullCred.emit(this.borrower.id);
    }

    runExperianCommercial(): void {
        this.triggerExperianCommercial.emit(this.borrower.id);
    }

    toggleVerification(document: Document) {
        this.updateDocDrsVerification.emit(document);
    }

    ocrolusDocTypeTooltip(document: Document): string | null {
        if (document.ocrolusDocTypeVerified === null) {
            return null;
        }

        if (document.ocrolusDocTypeVerified === 1) {
            return `Document category "${this.documentCategories.find(c => c.id === document.category)?.name}" verified by Ocrolus`;
        }

        if (document.ocrolusFormType === 'UNKNOWN') {
            return 'Document category marked unknown by Ocrolus';
        }

        if (document.ocrolusStatus === 'REJECTED') {
            return 'Document was rejected by Ocrolus and category could not be verified';
        }

        return null;
    }
}
